import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import TableSkeleton from '../TableSkeleton';
import EmptyTableIconV2 from '../../images/EmptyTableV2';
import {
  BORDER_SOFT,
  BORDER_STRONG,
  BottomSheet,
  CellContext,
  EmptyState,
  Icon,
  NoResults,
  ListBulkAction,
  ListColumn,
  ListEmptyState,
  ListFilter,
  ListRowAction,
  ListSortOption,
  SelectBox,
  SheetRow,
  SortDir,
  TEAL,
  TEAL_DARK,
  TEXT,
  TEXT_DIM,
  TEXT_MUTED,
  cellText,
  cellValue,
  filterValueLabel,
  fromSearchTime,
  t,
  toSearchTime, sortDirLabelOf } from './listShared';

export interface ListMobileSelection {
  selectMode: boolean;
  onEnterSelectMode: (id: string) => void;
  onExitSelectMode: () => void;
  selectedIds: string[];
  onToggleRow: (id: string) => void;
  onSelectAllPage: () => void;
  /** When absent (or empty) long-press does nothing and no select mode exists. */
  bulkActions?: ListBulkAction[];
}

export interface ListMobileProps<Row = any> {
  language: any;
  columns: ListColumn<Row>[];
  rows: Row[];
  rowKey?: (row: Row) => string;
  loading: boolean;
  count: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  /** Omit to hide the search box. */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  /** i18n key. */
  searchPlaceholder?: string;
  filters?: ListFilter[];
  filterValues?: Record<string, string>;
  onFilterChange?: (key: string, value: string) => void;
  onClearFilters?: () => void;
  sortBy?: string;
  sortDir?: SortDir;
  /**
   * The Sort row of the filter sheet. Rendered when `onSortChange` is given
   * and there is something to sort by: `sortOptions`, else the sortable columns.
   */
  sortOptions?: ListSortOption[];
  onSortChange?: (sortBy: string, sortDir: SortDir) => void;
  selection?: ListMobileSelection;
  /** Record-sheet entries. */
  rowActions?: ListRowAction<Row>[];
  /** Replaces the default record-sheet header (title / subtitle / trailing). */
  recordHeader?: (row: Row) => React.ReactNode;
  /** i18n key of the noun after the count on the filter sheet's Show button. */
  countNoun?: string;
  emptyState: ListEmptyState;
  /** True while a search or filter is active: an empty page shows "No Results Found" instead. */
  hasActiveQuery?: boolean;
}

/** Key of the Sort row inside the filter sheet (filters use their own keys). */
const SORT_ROW = '__sort';

/**
 * Height of the fixed select-mode action bar, reserved below the card list so
 * the last card can scroll clear of it. The bar sits on top of the host
 * shell's bottom tab bar: dev-core-web (feat/mobile-shell-nav and later)
 * defines --finex-tabbar-h on :root in src/index.css; an older shell without
 * it falls back to 0px and the bar simply sits at the bottom edge.
 */
const BULK_BAR_HEIGHT = 72;
// The host tab bar is --finex-tabbar-h plus the iPhone home-indicator inset.
const TABBAR_OFFSET = 'calc(var(--finex-tabbar-h, 0px) + env(safe-area-inset-bottom, 0px))';

const defaultRowKey = (row: any) => String(row?.id);

/**
 * The mobile list: a card list with a record sheet, an optional long-press
 * select mode and a filter sheet. Rendered instead of the shared
 * TableReportMobileV2, which cannot express this design.
 */
const ListMobile: React.FC<ListMobileProps> = ({
  language,
  columns,
  rows,
  rowKey = defaultRowKey,
  loading,
  count,
  page,
  limit,
  onPageChange,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search',
  filters = [],
  filterValues = {},
  onFilterChange,
  onClearFilters,
  sortBy = '',
  sortDir = 'DESC',
  sortOptions,
  onSortChange,
  selection,
  rowActions = [],
  recordHeader,
  countNoun = 'items',
  emptyState,
  hasActiveQuery = false,
}) => {
  const [recordId, setRecordId] = useState<string>('');
  const [filterSheetOpen, setFilterSheetOpen] = useState<boolean>(false);
  const [expandedFilter, setExpandedFilter] = useState<string | null>(null);
  const longPressTimer = useRef<any>(null);
  const longPressFired = useRef<boolean>(false);

  useEffect(
    () => () => {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
    },
    []
  );

  const selectable = !!selection && (selection.bulkActions?.length ?? 0) > 0;
  const selectMode = selectable && selection!.selectMode;
  const selectedIds = selection?.selectedIds ?? [];
  const selected = new Set(selectedIds.map(String));
  const record = rows.find(r => rowKey(r) === recordId);
  const totalPages = Math.max(1, Math.ceil(count / (limit || 50)));
  const activeFilters = filters.filter(f => !!filterValues[f.key]);
  const sorts: ListSortOption[] =
    sortOptions ??
    columns
      .filter(c => c.sortable && c.desktop !== false)
      .map(c => ({ value: c.sortKey ?? c.key, label: c.label }));
  const hasSort = !!onSortChange && sorts.length > 0;
  /** The sliders icon / Filters button opens the sheet when it has anything to show. */
  const hasFilters = filters.length > 0 || hasSort;

  const titleColumn = columns.find(c => c.mobile === 'title') ?? columns[0];
  const subtitleColumns = columns.filter(c => c.mobile === 'subtitle');
  const trailingColumns = columns.filter(c => c.mobile === 'trailing');
  const sheetColumns = columns.filter(
    c => c !== titleColumn && (c.mobile === 'sheet' || c.mobile === undefined)
  );

  const ctxFor = (column: ListColumn, index: number): CellContext => ({
    language,
    sortBy,
    sortDir,
    index,
    page,
    limit,
    column,
  });

  const titleOf = (row: any, index: number) =>
    titleColumn ? cellValue(titleColumn, row, ctxFor(titleColumn, index)) : '';
  const subtitlePartsOf = (row: any, index: number) =>
    subtitleColumns.map(c => cellText(c, row, ctxFor(c, index))).filter(s => s !== '');
  /** The subtitle columns joined with " · ", each part its own text node. */
  const subtitleOf = (row: any, index: number) =>
    subtitlePartsOf(row, index).map((part, i) => (
      <React.Fragment key={i}>
        {i > 0 && ' · '}
        {part}
      </React.Fragment>
    ));

  const startLongPress = (id: string) => {
    if (!selectable) return;
    longPressFired.current = false;
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      selection!.onEnterSelectMode(id);
    }, 500);
  };

  const cancelLongPress = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
  };

  const cardTapped = (id: string) => {
    if (longPressFired.current) {
      longPressFired.current = false;
      return;
    }
    if (selectMode) selection!.onToggleRow(id);
    else setRecordId(id);
  };

  const actionLabel = (action: ListRowAction, row: any) =>
    typeof action.label === 'function' ? action.label(row) : t(language, action.label, action.label);

  const optionPill = (label: string, active: boolean, onClick: () => void, key?: string) => (
    <div
      key={key ?? label}
      className="cursor-pointer font-inter"
      style={{
        padding: '8px 16px',
        borderRadius: '30px',
        fontSize: '14px',
        fontWeight: 500,
        border: `1px solid ${active ? TEAL : BORDER_STRONG}`,
        backgroundColor: active ? TEAL : '#FFFFFF',
        color: active ? '#FFFFFF' : TEXT,
      }}
      onClick={onClick}
    >
      {label}
    </div>
  );

  const sortDirLabel = (dir: SortDir) => sortDirLabelOf(language, dir, sortBy ?? '');

  /** The Sort row: current field + direction, expanding to pills for each. Applies immediately. */
  const sortRow = (last: boolean) => {
    const current = sorts.find(o => o.value === sortBy);
    const fieldLabel = current ? t(language, current.label, current.label) : '';
    return (
      <div key={SORT_ROW} style={{ borderBottom: last ? undefined : `1px solid ${BORDER_SOFT}` }}>
        <div
          className="flex items-center cursor-pointer"
          style={{ gap: '12px', minHeight: '54px', padding: '6px 20px' }}
          onClick={() => setExpandedFilter(expandedFilter === SORT_ROW ? null : SORT_ROW)}
        >
          <span
            className="font-inter"
            style={{ fontSize: '15px', color: TEXT_MUTED, flex: 'none', width: '96px' }}
          >
            {t(language, 'Sort', 'Sort')}
          </span>
          <span
            className="font-inter"
            style={{ fontSize: '15px', fontWeight: 500, flex: 1, textAlign: 'right', color: TEXT }}
          >
            {fieldLabel ? `${fieldLabel} · ${sortDirLabel(sortDir)}` : sortDirLabel(sortDir)}
          </span>
          <Icon name="chevd" size={16} />
        </div>
        {expandedFilter === SORT_ROW && (
          <div style={{ padding: '0 20px 12px' }}>
            <div className="flex" style={{ gap: '8px', flexWrap: 'wrap' }}>
              {sorts.map(option =>
                optionPill(
                  t(language, option.label, option.label),
                  option.value === sortBy,
                  () => onSortChange?.(option.value, sortDir),
                  `field-${option.value}`
                )
              )}
            </div>
            <div className="flex" style={{ gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
              {(['DESC', 'ASC'] as const).map(dir =>
                optionPill(sortDirLabel(dir), dir === sortDir, () => onSortChange?.(sortBy, dir), `dir-${dir}`)
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const filterRow = (filter: ListFilter, last: boolean) => (
    <div key={filter.key} style={{ borderBottom: last ? undefined : `1px solid ${BORDER_SOFT}` }}>
      <div
        className="flex items-center cursor-pointer"
        style={{ gap: '12px', minHeight: '54px', padding: '6px 20px' }}
        onClick={() => setExpandedFilter(expandedFilter === filter.key ? null : filter.key)}
      >
        <span
          className="font-inter"
          style={{ fontSize: '15px', color: TEXT_MUTED, flex: 'none', width: '96px' }}
        >
          {t(language, filter.label, filter.label)}
        </span>
        <span
          className="font-inter"
          style={{ fontSize: '15px', fontWeight: 500, flex: 1, textAlign: 'right', color: TEXT }}
        >
          {filterValues[filter.key] || filter.kind !== 'date'
            ? filterValueLabel(filter, filterValues[filter.key], language)
            : t(language, 'Any time', 'Any time')}
        </span>
        <Icon name="chevd" size={16} />
      </div>
      {expandedFilter === filter.key && (
        <div style={{ padding: '0 20px 12px' }}>
          {filter.kind === 'date' ? (
            <DatePicker
              inline
              selected={fromSearchTime(filterValues[filter.key] ?? '')}
              onChange={(d: any) => {
                onFilterChange?.(filter.key, d ? toSearchTime(d) : '');
                setExpandedFilter(null);
              }}
            />
          ) : filter.kind === 'text' ? (
            // Free-text filter (per-field server search): applied on Enter / Apply.
            <div className="flex items-center" style={{ gap: '8px' }}>
              <input
                autoFocus
                defaultValue={filterValues[filter.key] ?? ''}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    onFilterChange?.(filter.key, (e.target as HTMLInputElement).value.trim());
                    setExpandedFilter(null);
                  }
                }}
                className="font-inter placeholder:text-placeholder bg-transparent outline-none focus-none"
                style={{
                  // 16px: iOS zooms the page into any input under 16px.
                  fontSize: '16px',
                  flex: 1,
                  minWidth: 0,
                  height: '44px',
                  padding: '0 12px',
                  border: `1px solid ${BORDER_STRONG}`,
                  borderRadius: '12px',
                  color: TEXT,
                }}
                placeholder={t(language, 'Type to filter', 'Type to filter')}
              />
              <div
                className="cursor-pointer font-inter"
                style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#FFFFFF',
                  backgroundColor: TEAL,
                  borderRadius: '30px',
                  padding: '10px 16px',
                  whiteSpace: 'nowrap',
                }}
                onClick={e => {
                  const input = (e.currentTarget.parentElement as HTMLElement).querySelector('input');
                  onFilterChange?.(filter.key, (input?.value ?? '').trim());
                  setExpandedFilter(null);
                }}
              >
                {t(language, 'Apply', 'Apply')}
              </div>
            </div>
          ) : (
            <div className="flex" style={{ gap: '8px', flexWrap: 'wrap' }}>
              {(filter.options ?? []).map((option, index) => {
                const active = filterValues[filter.key] === option.value;
                return (
                  <div
                    key={`${index}-${option.value}`}
                    className="cursor-pointer font-inter"
                    style={{
                      padding: '8px 16px',
                      borderRadius: '30px',
                      fontSize: '14px',
                      fontWeight: 500,
                      border: `1px solid ${active ? TEAL : BORDER_STRONG}`,
                      backgroundColor: active ? TEAL : '#FFFFFF',
                      color: active ? '#FFFFFF' : TEXT,
                    }}
                    onClick={() => {
                      onFilterChange?.(filter.key, active ? '' : option.value);
                      setExpandedFilter(null);
                    }}
                  >
                    {option.label}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const recordIndex = record ? rows.indexOf(record) : 0;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        paddingBottom: selectMode ? `calc(${BULK_BAR_HEIGHT}px + ${TABBAR_OFFSET})` : undefined,
      }}
    >
      {selectMode ? (
        <div
          className="flex items-center"
          style={{
            gap: '12px',
            padding: '16px 0 12px',
            flex: 'none',
            borderBottom: `1px solid ${BORDER_SOFT}`,
          }}
        >
          <div className="cursor-pointer flex items-center" onClick={selection!.onExitSelectMode}>
            <Icon name="close" color={TEXT} size={22} />
          </div>
          <span className="font-inter" style={{ fontSize: '17px', fontWeight: 600, color: TEXT }}>
            {selectedIds.length} {t(language, 'selected', 'selected')}
          </span>
          <span
            className="cursor-pointer font-inter"
            style={{ marginLeft: 'auto', fontSize: '14px', fontWeight: 500, color: TEAL }}
            onClick={selection!.onSelectAllPage}
          >
            {t(language, 'Select all', 'Select all')} {rows.length}
          </span>
        </div>
      ) : (
        <>
          {onSearchChange && (
            <div
              className="flex items-center"
              style={{
                height: '44px',
                border: `1px solid ${BORDER_STRONG}`,
                borderRadius: '12px',
                gap: '8px',
                padding: hasFilters ? '0 8px 0 12px' : '0 12px',
                flex: 'none',
              }}
            >
              <Icon name="search" size={18} />
              <input
                value={searchValue ?? ''}
                onChange={e => onSearchChange(e.target.value)}
                placeholder={t(language, searchPlaceholder, searchPlaceholder)}
                className="font-inter placeholder:text-placeholder bg-transparent outline-none focus-none"
                // 16px: iOS zooms the page into any input under 16px.
                style={{ fontSize: '16px', flex: 1, minWidth: 0, color: TEXT }}
              />
              {hasFilters && (
                <div
                  className="flex items-center justify-center cursor-pointer"
                  style={{ width: '30px', height: '30px' }}
                  onClick={() => setFilterSheetOpen(true)}
                >
                  <Icon name="sliders" color={TEAL} size={18} />
                </div>
              )}
            </div>
          )}
          {!onSearchChange && hasFilters && (
            // No search box to host the sliders icon: a standalone Filters
            // button opens the same sheet.
            <div
              className="flex items-center cursor-pointer font-inter"
              style={{
                gap: '8px',
                height: '40px',
                padding: '0 14px',
                border: `1px solid ${BORDER_STRONG}`,
                borderRadius: '12px',
                width: 'fit-content',
                fontSize: '14px',
                fontWeight: 500,
                color: TEAL,
                flex: 'none',
              }}
              onClick={() => setFilterSheetOpen(true)}
            >
              <Icon name="sliders" color={TEAL} size={18} />
              <span>{t(language, 'Filters', 'Filters')}</span>
            </div>
          )}
          {activeFilters.length > 0 && (
            <div
              className="flex"
              style={{
                gap: '8px',
                paddingTop: '12px',
                flex: 'none',
                // One scrollable row, as on the desktop filter bar — more
                // filters never push the list down.
                overflowX: 'auto',
                scrollbarWidth: 'none',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {activeFilters.map(filter => (
                <div
                  key={filter.key}
                  className="flex items-center font-inter"
                  style={{
                    gap: '6px',
                    flex: 'none',
                    height: '32px',
                    padding: '0 10px 0 12px',
                    border: `1px solid ${BORDER_STRONG}`,
                    borderRadius: '999px',
                    backgroundColor: '#FFFFFF',
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ color: TEXT_MUTED }}>
                    {t(language, filter.label, filter.label)}:{' '}
                  </span>
                  <span style={{ color: TEXT, fontWeight: 500 }}>
                    {filterValueLabel(filter, filterValues[filter.key], language)}
                  </span>
                  <span
                    className="flex items-center"
                    onClick={() => onFilterChange?.(filter.key, '')}
                  >
                    <Icon name="close" size={14} />
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {selectMode && (
        <div className="font-inter" style={{ padding: '10px 0 0', fontSize: '12.5px', color: TEXT_DIM }}>
          {t(language, 'Selection applies to this page.', 'Selection applies to this page.')}
          {activeFilters.length > 0 && (
            <>
              {' '}
              {t(language, 'Filters', 'Filters')}:{' '}
              {activeFilters
                .map(filter => filterValueLabel(filter, filterValues[filter.key], language))
                .join(' · ')}
            </>
          )}
        </div>
      )}

      <div style={{ marginTop: onSearchChange || hasFilters || selectMode ? '12px' : undefined }}>
        {loading ? (
          <TableSkeleton rows={6} columns={2} />
        ) : rows.length === 0 ? (
          hasActiveQuery ? (
            <NoResults language={language} />
          ) : (
            <EmptyState state={emptyState} fallbackImg={<EmptyTableIconV2 />} />
          )
        ) : (
          rows.map((row: any, index: number) => {
            const id = rowKey(row);
            const isSelected = selectMode && selected.has(id);
            const subtitle = subtitlePartsOf(row, index).length > 0 ? subtitleOf(row, index) : null;
            return (
              <div
                key={id}
                className="flex items-center"
                style={{
                  gap: '12px',
                  minHeight: '64px',
                  padding: '10px 0',
                  borderBottom: `1px solid ${BORDER_SOFT}`,
                  backgroundColor: isSelected ? '#F3FAFA' : 'transparent',
                  userSelect: 'none',
                }}
                onTouchStart={() => startLongPress(id)}
                onTouchEnd={cancelLongPress}
                onTouchMove={cancelLongPress}
                onMouseDown={() => startLongPress(id)}
                onMouseUp={cancelLongPress}
                onMouseLeave={cancelLongPress}
                onContextMenu={e => e.preventDefault()}
                onClick={() => cardTapped(id)}
              >
                {selectMode && (
                  <div style={{ paddingRight: '2px' }}>
                    <SelectBox checked={isSelected} />
                  </div>
                )}
                <div className="flex flex-col" style={{ gap: '2px', minWidth: 0, flex: 1 }}>
                  <span
                    className="font-inter"
                    style={{
                      fontSize: '15px',
                      fontWeight: 500,
                      color: TEXT,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {titleOf(row, index)}
                  </span>
                  {subtitle && (
                    <span
                      className="font-inter"
                      style={{
                        fontSize: '12.5px',
                        color: TEXT_DIM,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {subtitle}
                    </span>
                  )}
                </div>
                {trailingColumns.length > 0 && (
                  <div className="flex flex-col items-end" style={{ gap: '4px' }}>
                    {trailingColumns.map(column => (
                      <React.Fragment key={column.key}>
                        {cellValue(column, row, ctxFor(column, index))}
                      </React.Fragment>
                    ))}
                  </div>
                )}
                {!selectMode && <Icon name="chev" color={BORDER_STRONG} size={16} />}
              </div>
            );
          })
        )}
      </div>

      {!loading && rows.length > 0 && totalPages > 1 && (
        <div
          className="flex items-center justify-between"
          style={{ padding: '14px 0 4px', flex: 'none' }}
        >
          <div
            className="flex items-center justify-center cursor-pointer"
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              border: `1px solid ${BORDER_STRONG}`,
              opacity: page <= 1 ? 0.4 : 1,
              transform: 'rotate(180deg)',
            }}
            onClick={() => page > 1 && onPageChange(page - 1)}
          >
            <Icon name="chev" size={16} />
          </div>
          <span className="font-inter" style={{ fontSize: '13px', color: TEXT_DIM }}>
            {t(language, 'Page', 'Page')} {page} {t(language, 'of', 'of')} {totalPages}
          </span>
          <div
            className="flex items-center justify-center cursor-pointer"
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              border: `1px solid ${BORDER_STRONG}`,
              opacity: page >= totalPages ? 0.4 : 1,
            }}
            onClick={() => page < totalPages && onPageChange(page + 1)}
          >
            <Icon name="chev" size={16} />
          </div>
        </div>
      )}

      {selectMode && (
        <div
          className="flex items-center"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: TABBAR_OFFSET,
            minHeight: `${BULK_BAR_HEIGHT}px`,
            boxSizing: 'border-box',
            backgroundColor: TEAL_DARK,
            color: '#FFFFFF',
            padding: '12px 16px 16px',
            gap: '10px',
            zIndex: 150,
          }}
        >
          <div className="flex" style={{ flex: 1, gap: '8px' }}>
            {selection!.bulkActions!.map((action, index) => (
              <div
                key={action.key}
                className="cursor-pointer font-inter"
                style={
                  index === 0
                    ? {
                        padding: '10px 18px',
                        borderRadius: '30px',
                        backgroundColor: 'rgba(255,255,255,.16)',
                        fontSize: '14px',
                        fontWeight: 600,
                      }
                    : {
                        padding: '10px 18px',
                        borderRadius: '30px',
                        border: '1px solid rgba(255,255,255,.35)',
                        fontSize: '14px',
                        fontWeight: 500,
                      }
                }
                onClick={() => action.onRun(selectedIds)}
              >
                {t(language, action.label, action.label)}
              </div>
            ))}
          </div>
        </div>
      )}

      <BottomSheet open={!!record} onClose={() => setRecordId('')}>
        {record && (
          <>
            {recordHeader ? (
              recordHeader(record)
            ) : (
              <div style={{ padding: '6px 20px 14px', borderBottom: `1px solid ${BORDER_SOFT}` }}>
                <div className="flex items-start" style={{ gap: '10px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      className="font-inter"
                      style={{ fontSize: '18px', fontWeight: 600, color: TEXT }}
                    >
                      {titleOf(record, recordIndex)}
                    </div>
                    {subtitlePartsOf(record, recordIndex).length > 0 && (
                      <div
                        className="font-inter"
                        style={{ fontSize: '13px', color: TEXT_DIM, marginTop: '2px' }}
                      >
                        {subtitleOf(record, recordIndex)}
                      </div>
                    )}
                  </div>
                  {trailingColumns.length > 0 && (
                    <div className="flex flex-col items-end" style={{ gap: '4px' }}>
                      {trailingColumns.map(column => (
                        <React.Fragment key={column.key}>
                          {cellValue(column, record, ctxFor(column, recordIndex))}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
                {sheetColumns.length > 0 && (
                  <div className="flex flex-col" style={{ gap: '8px', marginTop: '12px' }}>
                    {sheetColumns.map(column => (
                      <div
                        key={column.key}
                        className="flex items-center"
                        style={{ gap: '12px', minWidth: 0 }}
                      >
                        <span
                          className="font-inter"
                          style={{ fontSize: '13px', color: TEXT_DIM, flex: 'none', width: '110px' }}
                        >
                          {t(language, column.label, column.label)}
                        </span>
                        <div
                          className="font-inter"
                          style={{ fontSize: '13px', color: TEXT, flex: 1, minWidth: 0 }}
                        >
                          {column.render || column.mobileRender
                            ? cellValue(column, record, ctxFor(column, recordIndex))
                            : cellText(column, record, ctxFor(column, recordIndex))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {rowActions
              .filter(action => !action.hidden || !action.hidden(record))
              .map(action => (
              <SheetRow
                key={action.key}
                icon={action.sheetIcon ?? 'chev'}
                label={actionLabel(action, record)}
                sub={action.sub ? t(language, action.sub, action.sub) : undefined}
                danger={action.danger}
                onClick={() => {
                  setRecordId('');
                  action.onRun(record);
                }}
              />
            ))}
          </>
        )}
      </BottomSheet>

      {hasFilters && (
        <BottomSheet
          open={filterSheetOpen}
          onClose={() => {
            setFilterSheetOpen(false);
            setExpandedFilter(null);
          }}
          title={t(language, 'Filters', 'Filters')}
        >
          {hasSort && sortRow(filters.length === 0)}
          {filters.map((filter, index) => filterRow(filter, index === filters.length - 1))}
          <div className="flex items-center" style={{ gap: '12px', padding: '16px 20px 4px' }}>
            <div
              className="cursor-pointer font-inter"
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '12px 0',
                borderRadius: '30px',
                border: `1px solid ${BORDER_STRONG}`,
                fontSize: '14px',
                fontWeight: 500,
                color: TEXT,
              }}
              onClick={() => {
                onClearFilters?.();
                setExpandedFilter(null);
              }}
            >
              {t(language, 'Clear all', 'Clear all')}
            </div>
            <div
              className="cursor-pointer font-inter"
              style={{
                flex: 1.4,
                textAlign: 'center',
                padding: '12px 0',
                borderRadius: '30px',
                backgroundColor: TEAL,
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: 500,
              }}
              onClick={() => {
                setFilterSheetOpen(false);
                setExpandedFilter(null);
              }}
            >
              {t(language, 'Show', 'Show')} {count.toLocaleString()}{' '}
              {t(language, countNoun, countNoun)}
            </div>
          </div>
        </BottomSheet>
      )}
    </div>
  );
};

export default ListMobile;
